import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Layers, Cpu, ExternalLink, Camera, X } from 'lucide-react';
import { api } from '../lib/api';

const categories = [
  { label: 'All', value: '' },
  { label: 'Resistors', value: 'resistor' },
  { label: 'Capacitors', value: 'capacitor' },
  { label: 'ICs / Chips', value: 'integrated circuit' },
  { label: 'Transistors', value: 'transistor' },
  { label: 'MOSFETs', value: 'mosfet' },
  { label: 'LEDs', value: 'led' },
  { label: 'Diodes', value: 'diode' },
  { label: 'Inductors', value: 'inductor' },
  { label: 'Sensors', value: 'sensor' },
  { label: 'Modules', value: 'module' },
];

const popularComponents = [
  { name: '555 Timer IC', type: 'IC', value: 'NE555', desc: 'The most popular timer IC ever made' },
  { name: 'ATmega328P', type: 'MCU', value: 'ATmega328P', desc: 'Arduino Uno heart — 8-bit AVR microcontroller' },
  { name: 'LM7805', type: 'Regulator', value: 'LM7805', desc: '5V linear voltage regulator' },
  { name: 'IRF540N MOSFET', type: 'MOSFET', value: 'IRF540N', desc: 'N-channel power MOSFET, 100V, 33A' },
  { name: '2N2222', type: 'Transistor', value: '2N2222', desc: 'NPN general purpose transistor' },
  { name: 'ESP8266', type: 'WiFi Module', value: 'ESP8266', desc: 'Low-cost WiFi microcontroller module' },
];

const colorMap: Record<string, string> = {
  IC: 'var(--color-primary)',
  MCU: 'var(--color-secondary)',
  Regulator: 'var(--color-accent)',
  MOSFET: 'var(--color-purple)',
  Transistor: 'var(--color-secondary)',
  'WiFi Module': 'var(--color-primary)',
};

export default function Components() {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [visionResult, setVisionResult] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const initial = location.state?.initialQuery;
    if (initial) {
      setQuery(initial);
      handleSearch(initial);
    }
  }, [location.state]);

  const handleSearch = async (searchQuery: string = query) => {
    const q = (searchQuery + (category ? ` ${category}` : '')).trim();
    if (!q) return;
    setLoading(true);
    setSearched(true);
    setVisionResult(null);
    try {
      const data = await api.searchParts(q);
      setResults(data?.results || data?.parts || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setSearched(true);
    setResults([]);
    setVisionResult(null);

    try {
      const data = await api.analyzeImage(file, 'Identify this electronic component. Provide name, specs, and pinout if possible.');
      setVisionResult(data.summary || 'Analysis complete, but no details were returned.');
      // Auto-search for the identified part if we have keywords
      if (data.keywords && data.keywords.length > 0) {
        setQuery(data.keywords[0]);
        const searchData = await api.searchParts(data.keywords[0]);
        setResults(searchData?.results || searchData?.parts || []);
      }
    } catch (error: any) {
      setVisionResult(`AI identification failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', padding: '0 1.5rem 3rem' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Page header */}
        <div style={{ padding: '2.5rem 0 2rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
            <div style={{
              width: '52px', height: '52px',
              background: 'rgba(0,207,255,0.1)',
              border: '1px solid rgba(0,207,255,0.3)',
              borderRadius: '14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Layers size={24} color="var(--color-secondary)" />
            </div>
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '0.5rem' }}>
            Component <span style={{ color: 'var(--color-secondary)' }}>Lookup</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', maxWidth: '480px', margin: '0 auto', fontSize: '0.9rem' }}>
            Search by part number, function, or description. Find specs, pinouts, and datasheets.
          </p>
        </div>

        {/* Search bar */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid rgba(0,207,255,0.2)',
          borderRadius: '16px',
          padding: '1.25rem',
          marginBottom: '1.5rem',
          boxShadow: '0 0 20px rgba(0,207,255,0.05)',
        }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search: NE555, ATmega328P, 100uF capacitor..."
                style={{
                  width: '100%',
                  background: 'var(--bg-surface-2)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '10px',
                  padding: '0.75rem 4rem 0.75rem 2.5rem',
                  color: 'var(--text-primary)',
                  fontSize: '0.9rem',
                  outline: 'none',
                  fontFamily: 'var(--font-body)',
                  transition: 'border-color 0.2s ease',
                }}
                onFocus={e => { e.target.style.borderColor = 'rgba(0,207,255,0.4)'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--border-color)'; }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                title="Identify from Photo"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-secondary)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)'}
              >
                <Camera size={18} />
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                accept="image/*" 
                onChange={handleUpload}
              />
            </div>

            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              style={{
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border-color)',
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                color: 'var(--text-secondary)',
                fontSize: '0.85rem',
                cursor: 'pointer',
                outline: 'none',
                fontFamily: 'var(--font-body)',
              }}
            >
              {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>

            <button
              onClick={() => handleSearch()}
              disabled={loading || !query.trim()}
              style={{
                background: 'linear-gradient(135deg, #00cfff, #0099cc)',
                color: '#050912',
                border: 'none',
                borderRadius: '10px',
                padding: '0.75rem 1.5rem',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                opacity: !query.trim() || loading ? 0.5 : 1,
                transition: 'all 0.2s ease',
              }}
            >
              {loading ? '...' : <><Search size={15} /> Search</>}
            </button>
          </div>
        </div>

        {/* Vision Result or Uploading */}
        {uploading && (
          <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--bg-surface)', border: '1px solid var(--border-color)', borderRadius: '16px', marginBottom: '1.5rem' }}>
            <Cpu size={32} style={{ animation: 'spin-slow 2s linear infinite', marginBottom: '0.75rem', color: 'var(--color-primary)' }} />
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Analyzing component image...</p>
          </div>
        )}

        {visionResult && !uploading && (
          <div style={{
            background: 'rgba(0,255,136,0.05)',
            border: '1px solid rgba(0,255,136,0.3)',
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            position: 'relative',
            animation: 'slide-up 0.3s ease forwards',
          }}>
            <button 
              onClick={() => setVisionResult(null)}
              style={{ position: 'absolute', right: '12px', top: '12px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
            >
              <X size={16} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem' }}>
              <Cpu size={18} color="var(--color-primary)" />
              <span style={{ fontWeight: 700, color: 'var(--color-primary)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI Component Analysis</span>
            </div>
            <div style={{ fontSize: '0.92rem', color: 'var(--text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {visionResult}
            </div>
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(0,255,136,0.1)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Identified via Vision AI. Results are probabilistic. Always verify with part markings.
            </div>
          </div>
        )}

        {/* Results */}
        {searched && (
          <div style={{ marginBottom: '2rem' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                <Cpu size={32} style={{ animation: 'spin-slow 2s linear infinite', marginBottom: '0.75rem', color: 'var(--color-secondary)' }} />
                <p>Searching component databases...</p>
              </div>
            ) : results.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {results.map((r: any, i: number) => (
                  <div key={i} style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    padding: '1.25rem',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
                    transition: 'border-color 0.2s ease',
                  }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,207,255,0.3)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'}
                  >
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '0.2rem', color: 'var(--text-primary)' }}>{r.title || r.name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{r.description || r.source}</div>
                    </div>
                    {r.url && (
                      <a href={r.url} target="_blank" rel="noopener noreferrer"
                        style={{ color: 'var(--color-secondary)', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.3rem', textDecoration: 'none', flexShrink: 0 }}>
                        View <ExternalLink size={13} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                <p>No components found. Try a different search term.</p>
              </div>
            )}
          </div>
        )}

        {/* Popular Components */}
        {!searched && (
          <div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
              POPULAR COMPONENTS:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
              {popularComponents.map(({ name, type, value, desc }) => (
                <button
                  key={value}
                  onClick={() => { setQuery(value); handleSearch(value); }}
                  style={{
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '12px',
                    padding: '1rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,207,255,0.3)'; (e.currentTarget as HTMLElement).style.background = 'rgba(0,207,255,0.04)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)'; (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface)'; }}
                >
                  <span style={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    padding: '0.15rem 0.45rem',
                    borderRadius: '4px',
                    background: 'rgba(0,207,255,0.1)',
                    color: colorMap[type] || 'var(--color-secondary)',
                    border: `1px solid ${colorMap[type] || 'var(--color-secondary)'}44`,
                    fontFamily: 'var(--font-mono)',
                    flexShrink: 0,
                    marginTop: '1px',
                  }}>{type}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)', marginBottom: '0.2rem' }}>{name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
